import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Lock } from "lucide-react";
import { useAdminSupport } from "@/hooks/useAdminSupport";

/**
 * Inner guard for routes that require `super_admin`. `admin_support` users
 * see an editorial "no permission" panel instead of being kicked out of the
 * admin area entirely.
 */
export function AdminSupportGuard({ children }: { children: ReactNode }) {
  const { role, isLoading } = useAdminSupport();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (role === "super_admin") return <>{children}</>;

  if (role === "admin_support") {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
          style={{
            background: "rgba(194,84,26,0.08)",
            border: "1px solid rgba(194,84,26,0.18)",
          }}
        >
          <Lock className="w-5 h-5" style={{ color: "#c2541a" }} />
        </div>
        <div
          className="text-[9px] uppercase tracking-[0.32em] mb-2"
          style={{ color: "rgba(58, 53, 48, 0.55)" }}
        >
          Restricted
        </div>
        <h1
          className="text-[28px] mb-3"
          style={{
            fontFamily: "Outfit, sans-serif",
            fontWeight: 300,
            letterSpacing: "-0.01em",
            color: "#3a3530",
          }}
        >
          You don't have permission to access this
        </h1>
        <p className="text-[13px] text-muted-foreground max-w-md leading-relaxed">
          This area is restricted to super admins. Support admins can still use
          the rest of the control room from the sidebar.
        </p>
      </div>
    );
  }

  return <Navigate to="/dashboard" replace />;
}
